import { Box, Button, Chip, Drawer, Group, Pagination, SegmentedControl, Text } from "@mantine/core"
import { useDisclosure, useMediaQuery } from "@mantine/hooks"
import { ArrowLeftIcon, ShoppingCartSimpleIcon, WarningCircleIcon } from "@phosphor-icons/react"
import React from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router"
import useSWR from "swr"
import { ConsignmentOfferingApi, PricingApi } from "../../../apis"
import type { PriceBreakdown } from "../../../apis/pricing"
import { IAuth } from "../../../store/Auth"
import {
  addToCart as addToCartAction,
  CartItem,
  ICart,
  removeFromCart as removeFromCartAction,
  updatePrice as updatePriceAction,
  updateQuantity as updateQuantityAction,
} from "../../../store/Cart"
import { currencyFormat } from "../../../utils"
import { Dialog } from "../../../utils/confirm-modal"
import { message } from "../../../utils/message"
import { renderBusinessName } from "../../../utils/render-business-name"
import { PageLayout } from "../../layout"
import { ListState } from "../../ui"
import type { CategorySelection } from "../category-tree-filter"
import { ALL_SUPPLIERS, SupplierSelect } from "../supplier-select"
import { BrandFilter, BrandStrip, type BrandOption } from "./BrandBar"
import { CartDrawer } from "./CartDrawer"
import classes from "./catalog.module.css"
import { flattenCatalogue, useCatalogue } from "./catalogue-data"
import { FilterSidebar } from "./FilterSidebar"
import { buildPromoGroups, describePrice, hasPromotion, unavailablePrice, type PriceView } from "./price-labels"
import { ProductCard } from "./ProductCard"
import { ProductTable, TABLE_OPTIONAL_COLUMNS, type ProductRow } from "./ProductListItem"
import { ProductListSkeleton } from "./ProductListSkeleton"
import { PromoStrip } from "./PromotionBanner"
import { PromotionsView } from "./PromotionsView"
import { ActiveFilters, SearchBar } from "./SearchBar"
import type { FilterChip, FilterState, Props } from "./types"
import { EMPTY_FILTERS, GRID_PAGE_SIZE, TABLE_PAGE_SIZES } from "./types"
import { cleanName, getMinOrderQuantity, getProductPrice, LIST_STORAGE_KEY, matchesCategorySelection, stockLevel, type StockLevel } from "./utils"

export type { CartItem }

// Filter state survives a trip to a product page and back (sessionStorage),
// and is dropped when the tab closes.
const readStored = <T,>(key: string, fallback: T): T => {
  try {
    const raw = sessionStorage.getItem(`${LIST_STORAGE_KEY}.${key}`)
    if (raw == null) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

const writeStored = (key: string, value: unknown) => {
  try {
    sessionStorage.setItem(`${LIST_STORAGE_KEY}.${key}`, JSON.stringify(value))
  } catch {
    /* ignore quota */
  }
}

// Ticked category nodes; a single `category` saved before multi-select becomes a one-node selection.
const readCategories = (): CategorySelection[] => {
  const isSelection = (c: any): c is CategorySelection => typeof c?.id === "string" && Array.isArray(c.path)
  const stored = readStored<unknown>("categories", null)
  if (Array.isArray(stored)) return stored.filter(isSelection)
  const single = readStored<{ id?: string; name?: string } | null>("category", null)
  return single?.id && single.name ? [{ id: single.id, path: [single.name] }] : []
}

// Optional table columns the buyer can hide; remembered across sessions (localStorage).
const TABLE_COLUMNS_KEY = `${LIST_STORAGE_KEY}.tableColumns`
const readHiddenColumns = (): string[] => {
  try {
    const raw = localStorage.getItem(TABLE_COLUMNS_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === "string") : []
  } catch {
    return []
  }
}

// Older sessions stored a different filter shape; keep only the known fields.
const readFilters = (): FilterState => {
  const stored = readStored<Partial<FilterState>>("filters", {})
  const pick = <K extends keyof FilterState>(k: K): FilterState[K] =>
    stored && stored[k] !== undefined && typeof stored[k] === typeof EMPTY_FILTERS[k] ? (stored[k] as FilterState[K]) : EMPTY_FILTERS[k]
  return {
    brands: Array.isArray(stored?.brands) ? stored.brands : [],
    priceMin: typeof stored?.priceMin === "number" ? stored.priceMin : null,
    priceMax: typeof stored?.priceMax === "number" ? stored.priceMax : null,
    sort: pick("sort"),
    inStockOnly: pick("inStockOnly"),
    stockLevels: Array.isArray(stored?.stockLevels) ? stored.stockLevels : [],
    promoOnly: pick("promoOnly"),
    promotionId: typeof stored?.promotionId === "string" ? stored.promotionId : null,
  }
}

const brandOf = (p: any): { id: string; name: string; logo: string | null } | null => {
  const brand = p.item?.brand || p.brand
  if (!brand) return null
  if (typeof brand === "string") return { id: brand, name: brand, logo: null }
  return brand.id ? { id: brand.id, name: cleanName(brand.name), logo: brand.logo || null } : null
}

type FilterKey = "search" | "category" | "price" | "inStock" | "stockLevels" | "promo" | "brands"

export function ProductList({ suppliers }: Props) {
  const { t } = useTranslation(["order", "common"])
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const cart = useSelector((state: { cart: ICart }) => state.cart.items)
  const { user } = useSelector((state: { auth: IAuth }) => state.auth)
  const buyerBusinessId = user?.currentBusinessId || null
  const isMobile = useMediaQuery("(max-width: 62em)")

  const allSuppliers = React.useMemo(() => suppliers || [], [suppliers])

  // ── UI state ──
  const [supplierId, setSupplierId] = React.useState<string>(() => {
    const stored = readStored<string>("supplierId", "")
    if (stored === ALL_SUPPLIERS && allSuppliers.length > 1) return stored
    if (allSuppliers.some((s: any) => s.id === stored)) return stored
    return allSuppliers[0]?.id || ALL_SUPPLIERS
  })
  const [brandMenuOpen, setBrandMenuOpen] = React.useState(false)
  const [screen, setScreen] = React.useState<"catalog" | "promotions">(() => readStored("screen", "catalog"))
  const [search, setSearch] = React.useState(() => readStored<string>("search", ""))
  const [page, setPage] = React.useState(() => readStored<number>("page", 1))
  const [viewMode, setViewMode] = React.useState<"grid" | "list">(() => readStored<"grid" | "list">("viewMode", "grid"))
  const [tablePageSize, setTablePageSize] = React.useState<number>(() => readStored("tablePageSize", TABLE_PAGE_SIZES[1]))
  const [filters, setFilters] = React.useState<FilterState>(readFilters)
  const [categories, setCategories] = React.useState<CategorySelection[]>(readCategories)
  const [sourceFilter, setSourceFilter] = React.useState<"ALL" | "OWNED" | "CONSIGNMENT">(() => readStored("sourceFilter", "ALL"))
  const [filterDrawerOpened, { open: openFilterDrawer, close: closeFilterDrawer }] = useDisclosure(false)
  const [cartDrawerOpened, { open: openCartDrawer, close: closeCartDrawer }] = useDisclosure(false)

  React.useEffect(() => writeStored("supplierId", supplierId), [supplierId])
  React.useEffect(() => writeStored("screen", screen), [screen])
  React.useEffect(() => writeStored("search", search), [search])
  React.useEffect(() => writeStored("page", page), [page])
  const [hiddenColumns, setHiddenColumns] = React.useState<string[]>(readHiddenColumns)
  const toggleColumn = (key: string) =>
    setHiddenColumns((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      try {
        localStorage.setItem(TABLE_COLUMNS_KEY, JSON.stringify(next))
      } catch {
        /* ignore */
      }
      return next
    })
  React.useEffect(() => writeStored("viewMode", viewMode), [viewMode])
  React.useEffect(() => writeStored("tablePageSize", tablePageSize), [tablePageSize])
  React.useEffect(() => writeStored("filters", filters), [filters])
  React.useEffect(() => writeStored("categories", categories), [categories])
  React.useEffect(() => writeStored("sourceFilter", sourceFilter), [sourceFilter])

  const activeSuppliers = React.useMemo(
    () => (supplierId === ALL_SUPPLIERS ? allSuppliers : allSuppliers.filter((s: any) => s.id === supplierId)),
    [allSuppliers, supplierId],
  )
  const showSupplier = activeSuppliers.length > 1

  // How many promotions reach this buyer (price service, cheap): the strip's space is held while
  // the catalogue and its prices load, so the grid does not jump when the promotions arrive.
  const promoSupplierIds: string[] = activeSuppliers.slice(0, 5).map((s: any) => s.id)
  const { data: expectedPromos = 0 } = useSWR(
    promoSupplierIds.length ? ["catalog.promo-count", ...promoSupplierIds] : null,
    async () => {
      const counts = await Promise.all(
        promoSupplierIds.map((id) => PricingApi.buyerPromotions(id).then((r) => r?.rows?.length || 0).catch(() => 0)),
      )
      return counts.reduce((a, b) => a + b, 0)
    },
    { revalidateOnFocus: false },
  )

  // ── Data ──
  const { data: catalogue, isLoading, error: productsError, mutate: reloadProducts } = useCatalogue(activeSuppliers)
  const failedSuppliers = React.useMemo(() => catalogue?.failed ?? [], [catalogue])

  // A partial failure still shows a usable catalogue, but the buyer has to know it is incomplete.
  React.useEffect(() => {
    if (failedSuppliers.length > 0) message.error(t("order:productList.partialLoadFailed", { count: failedSuppliers.length }))
  }, [failedSuppliers, t])

  // R14 — consignment offering items configured for this retailer, merged into the grid.
  const { data: consignmentOfferingData } = useSWR(
    buyerBusinessId ? ["cng-offering.for-retailer", buyerBusinessId] : null,
    async () => {
      try {
        return await ConsignmentOfferingApi.forRetailer({ businessId: buyerBusinessId! })
      } catch {
        return { items: [] }
      }
    },
    { revalidateOnFocus: false },
  )

  const ownProducts = React.useMemo(() => flattenCatalogue(catalogue), [catalogue])

  const allProducts = React.useMemo(() => {
    const activeIds = new Set(activeSuppliers.map((s: any) => s.id))
    const consignmentItems = ((consignmentOfferingData as any)?.items ?? []) as Array<Record<string, any>>
    const consignmentProducts = consignmentItems
      .filter((item) => activeIds.has(item.supplierBusinessId))
      .map((item) => ({
        id: item.variantId ?? item.offeringItemId,
        variantId: item.variantId ?? item.offeringItemId,
        variantParentId: item.productId,
        skuCode: item.skuCode,
        nameMon: item.nameMon,
        image: item.image,
        category: item.category,
        brand: item.brand,
        supplierBusinessId: item.supplierBusinessId,
        supplierName: item.supplierName,
        consignmentSalePrice: item.salePrice,
        inventoryTotalAvialable: item.availableStock,
        isConsignment: true,
        consignmentOfferingId: item.offeringId,
        consignmentOfferingItemId: item.offeringItemId,
        consignmentAgreementId: item.consignmentAgreementId,
      }))
    return [...ownProducts, ...consignmentProducts]
  }, [ownProducts, consignmentOfferingData, activeSuppliers])

  // ── Prices (qty 1, per supplier) ──
  const [pricesLoading, setPricesLoading] = React.useState(true)
  const [breakdowns, setBreakdowns] = React.useState<Record<string, PriceBreakdown>>({})
  const [priceFailed, setPriceFailed] = React.useState<string[]>([])
  const [priceAttempt, setPriceAttempt] = React.useState(0)

  React.useEffect(() => {
    const groups = catalogue?.suppliers || []
    if (groups.length === 0) {
      setPricesLoading(false)
      return
    }
    let cancelled = false
    setPricesLoading(true)
    const run = async () => {
      const results = await Promise.all(
        groups.map(async (g) => {
          const products = g.products || []
          if (products.length === 0) return { supplierId: g.supplierId, rows: [] as PriceBreakdown[], ok: true }
          try {
            const res = await PricingApi.calculateBatch({
              businessId: g.supplierId,
              buyerBusinessId,
              variants: products.map((p: any) => ({ variantId: p.id, quantity: 1 })),
            })
            const rows: PriceBreakdown[] = Array.isArray(res) ? res : (res as any)?.rows || (res as any)?.data || []
            // /ord/price/calculate-batch answers [] when the price service fails
            // (de-order-service routes/api/price/calculate-batch.ts catch): one
            // breakdown per variant is the normal answer, so [] for a non-empty
            // list is a failure, not "no prices".
            return { supplierId: g.supplierId, rows, ok: rows.length > 0 }
          } catch {
            return { supplierId: g.supplierId, rows: [] as PriceBreakdown[], ok: false }
          }
        }),
      )
      if (cancelled) return
      const map: Record<string, PriceBreakdown> = {}
      results.forEach((r) => r.rows.forEach((b) => (map[b.variantId] = b)))
      setBreakdowns(map)
      setPriceFailed(results.filter((r) => !r.ok).map((r) => r.supplierId))
    }
    run().finally(() => {
      if (!cancelled) setPricesLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [catalogue, buyerBusinessId, priceAttempt])

  // ── Cart lines re-priced at their cart quantity (B02: TIER promotions price by quantity) ──
  const [cartBreakdowns, setCartBreakdowns] = React.useState<Record<string, PriceBreakdown>>({})
  const cartRef = React.useRef(cart)
  cartRef.current = cart
  const cartPriceKey = cart
    .filter((i) => !i.isConsignment)
    .map((i) => `${i.supplierBusinessId}:${i.id}:${i.quantity}`)
    .join("|")

  React.useEffect(() => {
    const items = cartRef.current.filter((i) => !i.isConsignment)
    if (items.length === 0) {
      setCartBreakdowns({})
      return
    }
    let cancelled = false
    const handle = setTimeout(async () => {
      const bySupplier = new Map<string, CartItem[]>()
      items.forEach((i) => bySupplier.set(i.supplierBusinessId, [...(bySupplier.get(i.supplierBusinessId) || []), i]))
      const map: Record<string, PriceBreakdown> = {}
      await Promise.all(
        Array.from(bySupplier.entries()).map(async ([sid, lines]) => {
          try {
            const res = await PricingApi.calculateBatch({
              businessId: sid,
              buyerBusinessId,
              variants: lines.map((l) => ({ variantId: l.id, quantity: l.quantity })),
            })
            const rows: PriceBreakdown[] = Array.isArray(res) ? res : (res as any)?.rows || []
            rows.forEach((b) => (map[`${sid}:${b.variantId}`] = b))
          } catch {
            /* keep the qty-1 price; the order is priced again on the server */
          }
        }),
      )
      if (cancelled) return
      setCartBreakdowns(map)
      cartRef.current.forEach((item) => {
        const b = map[`${item.supplierBusinessId}:${item.id}`]
        if (b && b.finalPrice > 0 && b.finalPrice !== item.price) {
          dispatch(updatePriceAction({ productId: item.id, supplierBusinessId: item.supplierBusinessId, price: b.finalPrice }))
        }
      })
    }, 400)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [cartPriceKey, buyerBusinessId, dispatch])

  const cartQtyOf = React.useCallback(
    (p: any) => cart.find((i) => i.id === p.id && i.supplierBusinessId === p.supplierBusinessId)?.quantity || 0,
    [cart],
  )

  const priceView = React.useCallback(
    (p: any): PriceView => {
      if (!p.isConsignment && priceFailed.includes(p.supplierBusinessId)) return unavailablePrice(t)
      const inCart = cartQtyOf(p) > 0 ? cartBreakdowns[`${p.supplierBusinessId}:${p.id}`] : undefined
      const b = inCart || breakdowns[p.id]
      return describePrice(t, b, b && b.finalPrice > 0 ? b.finalPrice : getProductPrice(p))
    },
    [priceFailed, cartQtyOf, cartBreakdowns, breakdowns, t],
  )

  const listPrice = React.useCallback(
    (p: any) => {
      if (!p.isConsignment && priceFailed.includes(p.supplierBusinessId)) return 0
      const b = breakdowns[p.id]
      return b && b.finalPrice > 0 ? b.finalPrice : getProductPrice(p)
    },
    [breakdowns, priceFailed],
  )

  // ── Promotions ──
  const promoGroups = React.useMemo(() => buildPromoGroups(allProducts, breakdowns), [allProducts, breakdowns])
  const promoProductIds = React.useMemo(() => {
    const g = promoGroups.find((x) => x.promotionId === filters.promotionId)
    return g ? new Set(g.products.map((p) => p.id)) : null
  }, [promoGroups, filters.promotionId])

  // ── Filtering ──
  const sourceProducts = React.useMemo(() => {
    if (sourceFilter === "OWNED") return allProducts.filter((p) => !p.isConsignment)
    if (sourceFilter === "CONSIGNMENT") return allProducts.filter((p) => p.isConsignment)
    return allProducts
  }, [allProducts, sourceFilter])

  const applyFilters = React.useCallback(
    (products: any[], except?: FilterKey) => {
      const q = search.trim().toLowerCase()
      const idSet = categories.length > 0 ? new Set(categories.map((c) => c.id)) : null
      const brandSet = filters.brands.length > 0 ? new Set(filters.brands) : null
      return products.filter((p) => {
        if (except !== "search" && q) {
          const hit =
            p.nameMon?.toLowerCase().includes(q) ||
            p.skuCode?.toLowerCase().includes(q) ||
            p.barCode?.toLowerCase().includes(q) ||
            p.supplierName?.toLowerCase().includes(q)
          if (!hit) return false
        }
        if (except !== "category" && idSet && !matchesCategorySelection(p, idSet, new Set())) return false
        if (except !== "price" && (filters.priceMin !== null || filters.priceMax !== null)) {
          const price = listPrice(p)
          if (filters.priceMin !== null && price < filters.priceMin) return false
          if (filters.priceMax !== null && price > filters.priceMax) return false
        }
        const stock = p.inventoryTotalAvialable || 0
        if (except !== "inStock" && filters.inStockOnly && stock <= 0) return false
        if (except !== "stockLevels" && filters.stockLevels.length > 0 && !filters.stockLevels.includes(stockLevel(stock))) return false
        if (except !== "promo") {
          if (filters.promoOnly && !hasPromotion(breakdowns[p.id])) return false
          if (promoProductIds && !promoProductIds.has(p.id)) return false
        }
        if (except !== "brands" && brandSet) {
          const b = brandOf(p)
          if (!b || !brandSet.has(b.id)) return false
        }
        return true
      })
    },
    [search, categories, filters, listPrice, breakdowns, promoProductIds],
  )

  const categoryProducts = React.useMemo(() => applyFilters(sourceProducts, "category"), [applyFilters, sourceProducts])
  // Cheapest and dearest price over the whole catalogue, shown as the price inputs' placeholders.
  // A product with no price yet (0) is left out, or the lower bound would always read 0.
  const priceBounds = React.useMemo(() => {
    let min = Infinity
    let max = 0
    for (const p of sourceProducts) {
      const price = listPrice(p)
      if (!(price > 0)) continue
      if (price < min) min = price
      if (price > max) max = price
    }
    return max > 0 ? { min: Math.floor(min), max: Math.ceil(max) } : null
  }, [sourceProducts, listPrice])

  const stockCounts = React.useMemo(() => {
    const counts: Record<StockLevel, number> = { ready: 0, low: 0, out: 0 }
    applyFilters(sourceProducts, "stockLevels").forEach((p) => counts[stockLevel(p.inventoryTotalAvialable || 0)]++)
    return counts
  }, [applyFilters, sourceProducts])

  const productsBeforeBrand = React.useMemo(() => applyFilters(sourceProducts, "brands"), [applyFilters, sourceProducts])

  const brandOptions = React.useMemo<BrandOption[]>(() => {
    const map = new Map<string, BrandOption>()
    productsBeforeBrand.forEach((p) => {
      const b = brandOf(p)
      if (!b) return
      const cur = map.get(b.id) || { id: b.id, name: b.name, logo: b.logo, count: 0 }
      cur.count++
      map.set(b.id, cur)
    })
    return Array.from(map.values())
  }, [productsBeforeBrand])

  const countForBrands = React.useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return productsBeforeBrand.length
      const set = new Set(ids)
      return productsBeforeBrand.filter((p) => {
        const b = brandOf(p)
        return !!b && set.has(b.id)
      }).length
    },
    [productsBeforeBrand],
  )

  const filteredProducts = React.useMemo(() => {
    const result = applyFilters(sourceProducts)
    const byName = (a: any, b: any) => (a.nameMon || "").localeCompare(b.nameMon || "")
    switch (filters.sort) {
      case "name-desc":
        return result.sort((a, b) => byName(b, a))
      case "price-asc":
        return result.sort((a, b) => listPrice(a) - listPrice(b))
      case "price-desc":
        return result.sort((a, b) => listPrice(b) - listPrice(a))
      case "stock-desc":
        return result.sort((a, b) => (b.inventoryTotalAvialable || 0) - (a.inventoryTotalAvialable || 0))
      default:
        return result.sort(byName)
    }
  }, [applyFilters, sourceProducts, filters.sort, listPrice])

  // ── Paging ──
  const pageSize = viewMode === "grid" ? GRID_PAGE_SIZE : tablePageSize
  const filterSignature = JSON.stringify([search, filters, categories, supplierId, sourceFilter, viewMode, tablePageSize])
  const firstRender = React.useRef(true)
  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    setPage(1)
  }, [filterSignature])

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize))
  const safePage = Math.min(page, totalPages)
  const pageProducts = filteredProducts.slice((safePage - 1) * pageSize, safePage * pageSize)
  const rangeFrom = filteredProducts.length === 0 ? 0 : (safePage - 1) * pageSize + 1
  const rangeTo = Math.min(safePage * pageSize, filteredProducts.length)

  // ── Filter chips ──
  const activeChips = React.useMemo<FilterChip[]>(() => {
    const chips: FilterChip[] = []
    // The last two levels: a leaf alone ("75") does not say which category it is.
    categories.forEach((c) => chips.push({ type: "category", value: c.id, label: c.path.slice(-2).join(" › ") }))
    filters.brands.forEach((id) => {
      const b = brandOptions.find((x) => x.id === id)
      chips.push({ type: "brand", value: id, label: b?.name || id })
    })
    if (filters.priceMin !== null || filters.priceMax !== null) {
      chips.push({
        type: "price",
        value: "price",
        label: t("order:catalog.priceChip", {
          min: filters.priceMin !== null ? currencyFormat(filters.priceMin) : "0₮",
          max: filters.priceMax !== null ? currencyFormat(filters.priceMax) : "-",
        }),
      })
    }
    const legend: Record<StockLevel, string> = {
      ready: t("order:catalog.stockReadyLegend"),
      low: t("order:catalog.stockLowLegend"),
      out: t("order:catalog.stockOutLegend"),
    }
    filters.stockLevels.forEach((l) => chips.push({ type: "stock", value: l, label: legend[l] }))
    if (filters.promotionId) {
      const g = promoGroups.find((x) => x.promotionId === filters.promotionId)
      chips.push({ type: "promotion", value: filters.promotionId, label: t("order:catalog.promotionChip", { name: g?.name || "-" }) })
    }
    return chips
  }, [categories, filters, brandOptions, promoGroups, t])

  const removeChip = (chip: FilterChip) => {
    if (chip.type === "category") setCategories((prev) => prev.filter((c) => c.id !== chip.value))
    if (chip.type === "brand") setFilters((f) => ({ ...f, brands: f.brands.filter((b) => b !== chip.value) }))
    if (chip.type === "price") setFilters((f) => ({ ...f, priceMin: null, priceMax: null }))
    if (chip.type === "stock") setFilters((f) => ({ ...f, stockLevels: f.stockLevels.filter((l) => l !== chip.value) }))
    if (chip.type === "promotion") setFilters((f) => ({ ...f, promotionId: null }))
  }

  const hasActiveFilters = activeChips.length > 0 || filters.promoOnly || filters.inStockOnly || !!search.trim()

  const clearAllFilters = () => {
    setFilters((f) => ({ ...EMPTY_FILTERS, sort: f.sort }))
    setCategories([])
    setSearch("")
  }

  // ── Cart actions ──
  const addToCart = React.useCallback(
    (product: any, quantity: number) => {
      const current = cartQtyOf(product)
      if (current > 0) {
        dispatch(updateQuantityAction({ productId: product.id, supplierBusinessId: product.supplierBusinessId, quantity: current + quantity }))
        return
      }
      dispatch(
        addToCartAction({
          ...product,
          price: listPrice(product),
          quantity,
          variantId: product.id,
          variantParentId: product.id,
          supplierBusinessId: product.supplierBusinessId,
          supplierName: product.supplierName,
        }),
      )
    },
    [cartQtyOf, dispatch, listPrice],
  )

  const setQuantity = (product: any, quantity: number, row: ProductRow) => {
    const current = row.cartQty
    let next = quantity
    if (next > 0 && next < row.minOrder) next = next < current ? 0 : row.minOrder
    if (next <= 0) {
      dispatch(removeFromCartAction({ productId: product.id, supplierBusinessId: product.supplierBusinessId }))
      return
    }
    if (current === 0) addToCart(product, next)
    else dispatch(updateQuantityAction({ productId: product.id, supplierBusinessId: product.supplierBusinessId, quantity: next }))
  }

  const cartBySupplier = React.useMemo(() => {
    const grouped: Record<string, { supplierName: string; items: CartItem[]; totalAmount: number; totalItems: number }> = {}
    cart.forEach((item) => {
      if (!grouped[item.supplierBusinessId]) grouped[item.supplierBusinessId] = { supplierName: item.supplierName, items: [], totalAmount: 0, totalItems: 0 }
      grouped[item.supplierBusinessId].items.push(item)
      grouped[item.supplierBusinessId].totalAmount += item.price * item.quantity
      grouped[item.supplierBusinessId].totalItems += item.quantity
    })
    return grouped
  }, [cart])

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0)
  const totalAmount = cart.reduce((sum, item) => sum + item.price * item.quantity, 0)

  // Lines of the suppliers on screen (the table's cart bar).
  const activeIds = React.useMemo(() => new Set(activeSuppliers.map((s: any) => s.id)), [activeSuppliers])
  const screenCart = cart.filter((i) => activeIds.has(i.supplierBusinessId))
  const screenCartAmount = screenCart.reduce((sum, i) => sum + i.price * i.quantity, 0)

  const clearScreenCart = () =>
    Dialog.confirm(t("order:catalog.clearQtyConfirm"), (key) => {
      if (key !== "confirm") return
      screenCart.forEach((i) => dispatch(removeFromCartAction({ productId: i.id, supplierBusinessId: i.supplierBusinessId })))
    })

  // Header cart icon click.
  React.useEffect(() => {
    const handler = () => openCartDrawer()
    window.addEventListener("toggle-cart-drawer", handler)
    return () => window.removeEventListener("toggle-cart-drawer", handler)
  }, [openCartDrawer])

  // Navigated here from the header cart on another page: open the drawer.
  React.useEffect(() => {
    const state = location.state as { openCart?: boolean } | null
    if (state?.openCart) {
      openCartDrawer()
      navigate(location.pathname, { replace: true })
    }
  }, [location.state, location.pathname, navigate, openCartDrawer])

  const openProduct = React.useCallback(
    (product: any) => {
      navigate(`/buyer-sales/product/${product.id}?supplier=${product.supplierBusinessId}`, {
        state: { product: { ...product, price: listPrice(product) }, isMultiSupplier: allSuppliers.length > 1 },
      })
    },
    [navigate, listPrice, allSuppliers.length],
  )

  const openPromotion = (promotionId: string | null) => {
    setFilters((f) => ({ ...f, promotionId }))
    setScreen("catalog")
  }

  // ── Render ──
  const supplierPicker = (
    <SupplierSelect
      suppliers={allSuppliers}
      value={supplierId}
      allowAll
      onChange={(id) => {
        setSupplierId(id)
        setFilters((f) => ({ ...f, promotionId: null, brands: [] }))
        setCategories([])
      }}
    />
  )

  const breadcrumbs =
    screen === "promotions"
      ? [
          { to: "/", label: t("common:breadcrumb.home") },
          { to: "/buyer-sales", label: t("order:buyerSales.breadcrumb") },
          { label: t("order:catalog.title") },
          { label: t("order:catalog.promotions.title") },
        ]
      : [
          { to: "/", label: t("common:breadcrumb.home") },
          { to: "/buyer-sales", label: t("order:buyerSales.breadcrumb") },
          { label: t("order:catalog.title") },
        ]

  const title =
    screen === "promotions" ? (
      t("order:catalog.promotions.title")
    ) : (
      <Box component="span" display="inline-flex" style={{ alignItems: "center", gap: "var(--mantine-spacing-md)", flexWrap: "wrap" }}>
        <Box component="span">{t("order:catalog.title")}</Box>
        {supplierPicker}
      </Box>
    )

  const supplierQuery = supplierId && supplierId !== ALL_SUPPLIERS ? `?supplier=${supplierId}` : ""
  const extra =
    screen === "promotions"
      ? [
          <Button key="back" variant="default" radius="md" leftSection={<ArrowLeftIcon size={16} />} onClick={() => setScreen("catalog")}>
            {t("order:catalog.promotions.back")}
          </Button>,
        ]
      : [
          // B25: both screens open on the supplier picked here (or the first when «all»).
          <Button key="quick" variant="default" radius="md" size="md" onClick={() => navigate(`/buyer-sales/quick-order${supplierQuery}`)}>
            {t("bulkOrder:quick.title")}
          </Button>,
          <Button key="saved" variant="default" radius="md" size="md" onClick={() => navigate(`/buyer-sales/saved-lists${supplierQuery}`)}>
            {t("bulkOrder:saved.title")}
          </Button>,
          // B27: the supplier's promotions from the price service (canvas OrdB_PromoPage).
          <Button key="promotions" variant="default" radius="md" size="md" onClick={() => navigate(`/buyer-sales/promotions${supplierQuery}`)}>
            {t("order:catalog.promotions.title")}
          </Button>,
          <SegmentedControl
            key="view"
            value={viewMode}
            onChange={(v) => setViewMode(v as "grid" | "list")}
            radius="md"
            size="md"
            data={[
              { value: "grid", label: t("order:catalog.viewCard") },
              { value: "list", label: t("order:catalog.viewTable") },
            ]}
          />,
        ]

  const sidebar = (
    <FilterSidebar
      filters={filters}
      onFiltersChange={setFilters}
      categoryProducts={categoryProducts}
      stockCounts={stockCounts}
      categories={categories}
      onCategoriesChange={setCategories}
      priceBounds={priceBounds}
    />
  )

  const cartDrawer = (
    <CartDrawer
      opened={cartDrawerOpened}
      onClose={closeCartDrawer}
      cart={cart}
      cartBySupplier={cartBySupplier}
      totalItems={totalItems}
      totalAmount={totalAmount}
      breakdowns={cartBreakdowns}
      onUpdateQuantity={(productId, supplierBusinessId, quantity) =>
        dispatch(updateQuantityAction({ productId, supplierBusinessId, quantity }))
      }
      onRemoveFromCart={(productId, supplierBusinessId) => dispatch(removeFromCartAction({ productId, supplierBusinessId }))}
    />
  )

  if (isLoading && allProducts.length === 0) {
    return (
      <PageLayout title={title} breadcrumb={breadcrumbs} extra={extra} noFrame>
        <ProductListSkeleton viewMode={viewMode} promoCount={expectedPromos} />
        {cartDrawer}
      </PageLayout>
    )
  }

  if (screen === "promotions") {
    return (
      <PageLayout title={title} breadcrumb={breadcrumbs} extra={extra} noFrame>
        <PromotionsView groups={promoGroups} supplierPicker={supplierPicker} onOpenPromotion={(id) => openPromotion(id)} />
        {cartDrawer}
      </PageLayout>
    )
  }

  const rows: ProductRow[] = pageProducts.map((product) => ({
    product,
    view: priceView(product),
    cartQty: cartQtyOf(product),
    stock: product.inventoryTotalAvialable || 0,
    minOrder: getMinOrderQuantity(product),
  }))

  const brandSummary =
    filters.brands.length === 0
      ? t("order:catalog.brandSummaryAll", { count: brandOptions.length })
      : filters.brands.map((id) => brandOptions.find((b) => b.id === id)?.name || id).join(", ")

  return (
    <PageLayout title={title} breadcrumb={breadcrumbs} extra={extra} noFrame>
      <Drawer opened={filterDrawerOpened} onClose={closeFilterDrawer} title={t("common:filter.title")} size="sm" padding="md">
        {sidebar}
      </Drawer>

      {viewMode === "grid" && (
      <PromoStrip
        groups={promoGroups}
        expected={pricesLoading ? expectedPromos : 0}
        activeId={filters.promotionId}
        onSelect={(id) => setFilters((f) => ({ ...f, promotionId: id }))}
        onViewAll={() => setScreen("promotions")}
      />
      )}

      <ActiveFilters chips={activeChips} onRemove={removeChip} onClearAll={clearAllFilters} />

      {/* Table view is full width (canvas OrdB_CatalogTable); its filters open in the drawer. */}
      <Box className={classes.layout} data-full={viewMode === "list" || isMobile || undefined}>
        {!isMobile && viewMode === "grid" && sidebar}

        <Box miw={0} style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-md)" }}>
          <SearchBar
            search={search}
            onSearchChange={setSearch}
            brandFilter={
              <BrandFilter
                brands={brandOptions}
                selected={filters.brands}
                onApply={(ids) => setFilters((f) => ({ ...f, brands: ids }))}
                countFor={countForBrands}
                opened={brandMenuOpen}
                onOpenedChange={setBrandMenuOpen}
              />
            }
            promoOnly={filters.promoOnly}
            onPromoOnlyChange={(v) => setFilters((f) => ({ ...f, promoOnly: v }))}
            inStockOnly={filters.inStockOnly}
            onInStockOnlyChange={(v) => setFilters((f) => ({ ...f, inStockOnly: v }))}
            sort={filters.sort}
            onSortChange={(v) => setFilters((f) => ({ ...f, sort: v }))}
            pageSize={viewMode === "list" ? tablePageSize : undefined}
            onPageSizeChange={viewMode === "list" ? setTablePageSize : undefined}
            columns={
              viewMode === "list"
                ? TABLE_OPTIONAL_COLUMNS.map((key) => ({ key, label: t(`order:catalog.col.${key}`), visible: !hiddenColumns.includes(key) }))
                : undefined
            }
            onToggleColumn={viewMode === "list" ? toggleColumn : undefined}
            onOpenFilters={isMobile || viewMode === "list" ? openFilterDrawer : undefined}
          />

          {viewMode === "grid" && (
            <BrandStrip
              brands={brandOptions}
              selected={filters.brands}
              onToggle={(id) =>
                setFilters((f) => ({ ...f, brands: f.brands.includes(id) ? f.brands.filter((b) => b !== id) : [...f.brands, id] }))
              }
              onShowAll={() => setBrandMenuOpen(true)}
            />
          )}

          {allProducts.some((p) => p.isConsignment) && (
            <Chip.Group value={sourceFilter} onChange={(v) => setSourceFilter(((v as string) || "ALL") as "ALL" | "OWNED" | "CONSIGNMENT")}>
              <Group gap="xs">
                <Chip value="ALL" variant="outline" color="dark">
                  {t("order:productList.sourceAll")}
                </Chip>
                <Chip value="OWNED" variant="outline" color="dark">
                  {t("order:productList.sourceOwned")}
                </Chip>
                <Chip value="CONSIGNMENT" variant="outline" color="dark">
                  {t("order:productList.sourceConsignment")}
                </Chip>
              </Group>
            </Chip.Group>
          )}

          {priceFailed.length > 0 && (
            <Box className={classes.notice} role="status">
              <WarningCircleIcon size={18} />
              <Text span inherit style={{ flex: 1 }}>
                {t("order:catalog.priceFailed")}
              </Text>
              <Button variant="default" size="sm" radius="md" onClick={() => setPriceAttempt((n) => n + 1)}>
                {t("order:productList.retry")}
              </Button>
            </Box>
          )}

          <Group justify="space-between" gap="sm">
            <Text fz="var(--nw-fz-body)">
              <Text span inherit fw={650}>
                {t("order:catalog.productsCount", { count: filteredProducts.length })}
              </Text>
              {` · ${
                categories.length === 0
                  ? t("order:catalog.categoryAll")
                  : categories.length === 1
                    ? categories[0].path.join(" › ")
                    : t("order:catalog.categoryCount", { count: categories.length })
              }`}
            </Text>
            {viewMode === "list" ? (
              <Text className={classes.meta}>
                <Box component="span" className={classes.kbd}>
                  Enter
                </Box>{" "}
                {t("order:catalog.keyHintEnter")}{" "}
                <Box component="span" className={classes.kbd}>
                  ↑
                </Box>{" "}
                <Box component="span" className={classes.kbd}>
                  ↓
                </Box>{" "}
                {t("order:catalog.keyHintArrows")}
              </Text>
            ) : (
              <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                {t("order:catalog.brandSummary")}{" "}
                <Text span inherit fw={650} c="var(--nw-text)">
                  {brandSummary}
                </Text>
              </Text>
            )}
          </Group>

          {productsError && !isLoading ? (
            <ListState state="error" title={t("order:productList.loadFailed")} description={t("order:productList.loadFailedHint")} onRetry={() => reloadProducts()} />
          ) : filteredProducts.length === 0 ? (
            hasActiveFilters ? (
              <ListState state="noResults" title={t("order:productList.noProducts")} description={t("order:productList.noProductsHint")} action={{ label: t("order:catalog.clearAll"), onClick: clearAllFilters }} />
            ) : (
              <ListState state="empty" title={t("order:productList.noProducts")} />
            )
          ) : viewMode === "grid" ? (
            <Box className={classes.productGrid}>
              {rows.map((row) => (
                <ProductCard
                  key={`${row.product.supplierBusinessId}-${row.product.id}`}
                  product={row.product}
                  view={row.view}
                  priceLoading={pricesLoading}
                  cartQty={row.cartQty}
                  stock={row.stock}
                  minOrder={row.minOrder}
                  showSupplier={showSupplier}
                  onAdd={(qty) => addToCart(row.product, qty)}
                  onOpen={() => openProduct(row.product)}
                />
              ))}
            </Box>
          ) : (
            <ProductTable hiddenColumns={hiddenColumns} rows={rows} priceLoading={pricesLoading} showSupplier={showSupplier} onSetQty={setQuantity} onOpen={openProduct} />
          )}

          {filteredProducts.length > 0 && (
            <Group justify="space-between">
              <Text className={`${classes.meta} ${classes.mono}`}>
                {t("order:catalog.pageRange", { from: rangeFrom, to: rangeTo, total: filteredProducts.length })}
              </Text>
              {totalPages > 1 && <Pagination total={totalPages} value={safePage} onChange={setPage} color="dark" radius="md" />}
            </Group>
          )}

          {viewMode === "list" && screenCart.length > 0 && (
            <Box className={classes.cartBar}>
              <Text fz="var(--nw-fz-lead)" fw={650}>
                {t("order:catalog.cartBar", { lines: screenCart.length, amount: currencyFormat(screenCartAmount) })}
              </Text>
              <Text className={classes.meta} style={{ flex: 1 }}>
                {activeSuppliers.length === 1 ? `${renderBusinessName(activeSuppliers[0], "")} · ` : ""}
                {t("order:catalog.cartBarNote")}
              </Text>
              <Group gap="sm">
                <Button variant="default" radius="md" onClick={clearScreenCart}>
                  {t("order:catalog.clearQty")}
                </Button>
                <Button radius="md" leftSection={<ShoppingCartSimpleIcon size={16} />} onClick={openCartDrawer}>
                  {t("order:catalog.openCart")}
                </Button>
              </Group>
            </Box>
          )}
        </Box>
      </Box>

      {cartDrawer}
    </PageLayout>
  )
}
